import { z } from 'zod';

/**
 * Colours are stored either as RGB hex (screen/photographic intent) or as an
 * explicit CMYK build (0–100 %) for print-defined text and shapes, e.g. K-only
 * black for small text and grid lines. The browser preview of a CMYK colour is
 * an approximation only — it is not a colour proof.
 */
export const RgbColorSchema = z.object({ space: z.literal('rgb'), hex: z.string().regex(/^#[0-9a-fA-F]{6}$/) });
export const CmykColorSchema = z.object({
  space: z.literal('cmyk'),
  c: z.number().min(0).max(100),
  m: z.number().min(0).max(100),
  y: z.number().min(0).max(100),
  k: z.number().min(0).max(100),
});
export const ColorSchema = z.discriminatedUnion('space', [RgbColorSchema, CmykColorSchema]);
export type Color = z.infer<typeof ColorSchema>;

export const rgb = (hex: string): Color => ({ space: 'rgb', hex: hex.toLowerCase() });
export const cmyk = (c: number, m: number, y: number, k: number): Color => ({ space: 'cmyk', c, m, y, k });
export const K_BLACK: Color = cmyk(0, 0, 0, 100);
export const WHITE: Color = rgb('#ffffff');

/** Naive, device-independent CMYK→RGB used for on-screen preview only. */
export function cmykToRgb(c: number, m: number, y: number, k: number): [number, number, number] {
  const C = c / 100, M = m / 100, Y = y / 100, K = k / 100;
  return [255 * (1 - C) * (1 - K), 255 * (1 - M) * (1 - K), 255 * (1 - Y) * (1 - K)].map((v) => Math.round(v)) as [
    number,
    number,
    number,
  ];
}

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

export function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
}

/** Approximate CSS colour for preview. */
export function toCss(color: Color | null | undefined): string {
  if (!color) return 'none';
  if (color.space === 'rgb') return color.hex;
  return rgbToHex(...cmykToRgb(color.c, color.m, color.y, color.k));
}

/** Relative luminance (0..1) of the preview colour; used for legibility hints. */
export function luminance(color: Color): number {
  const [r, g, b] = color.space === 'rgb' ? hexToRgb(color.hex) : cmykToRgb(color.c, color.m, color.y, color.k);
  const f = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

export function describeColor(color: Color | null): string {
  if (!color) return 'None';
  if (color.space === 'rgb') return color.hex.toUpperCase();
  return `C${color.c} M${color.m} Y${color.y} K${color.k}`;
}
